import type { Dispatch, SetStateAction } from "react"

import { ProductSheetLanguages } from "@tfg/types/constants/language.constants"
import type { ComponentContent } from "@tfg/types/schemas/communication-studio/components"
import type { ContentFilteringProps } from "@tfg/types/types/communication-studio/configurations.types"

import {
  hasValidationErrors,
  getValidationErrorMessages,
  ComponentValidationResults,
  useGetRenderer,
  RenderableType,
} from "~/pages/Brand/CommunicationStudio/Content/config/component-renderer"
import { useComponentCreation } from "~/pages/Brand/CommunicationStudio/contexts/componentCreation/useComponentCreation"

export interface ComponentTypeRendererProps {
  componentType: RenderableType
  textContent: ComponentContent | null
  setTextContent: Dispatch<SetStateAction<ComponentContent | null>>
  updateTextContent: (
    blockIndex: number,
    textIndex: number,
    newValue: string
  ) => void
  configurations?: Partial<ContentFilteringProps> | null
  setConfigurations?: Dispatch<
    SetStateAction<Partial<ContentFilteringProps> | null>
  >
  validations?: ComponentValidationResults | null
  // Optional override for updateModalConfigurations (e.g., for content filtering)
  updateModalConfigurations?: (
    updatedConfigs: Partial<ContentFilteringProps> | null
  ) => void
  // Additional props that some renderers might need
  additionalProps?: Record<string, unknown>
}

export const ComponentTypeRenderer = ({
  componentType,
  textContent,
  setTextContent,
  updateTextContent,
  configurations,
  setConfigurations,
  validations: passedValidations,
  updateModalConfigurations: propUpdateModalConfigurations,
  additionalProps = {},
}: ComponentTypeRendererProps) => {
  const { updateModalConfigurations: contextUpdateModalConfigurations } =
    useComponentCreation()

  // Use prop if provided, otherwise fall back to context
  const updateModalConfigurations =
    propUpdateModalConfigurations || contextUpdateModalConfigurations

  const rendererMetadata = useGetRenderer(componentType)
  if (!rendererMetadata) {
    console.warn(`No renderer found for type: ${componentType}`)
    return null
  }

  const { renderer: RendererComponent, propsMapper } = rendererMetadata

  const validations = passedValidations || {
    content: undefined,
    configurations: undefined,
  }

  if (process.env.NODE_ENV !== "prod") {
    if (validations.content && hasValidationErrors(validations.content)) {
      console.warn(
        `Content validation errors for ${componentType}:`,
        getValidationErrorMessages(validations.content.errors)
      )
    }
    if (
      validations.configurations &&
      hasValidationErrors(validations.configurations)
    ) {
      console.warn(
        `Configuration validation errors for ${componentType}:`,
        getValidationErrorMessages(validations.configurations.errors)
      )
    }
  }

  const commonProps = {
    textContent,
    setTextContent,
    updateTextContent,
    componentType,
    language: ProductSheetLanguages.EN,
    configurations,
    setConfigurations,
    updateModalConfigurations,
  }

  const rendererProps = propsMapper
    ? propsMapper(commonProps, validations, rendererMetadata)
    : {
        ...commonProps,
        updateModalConfigurations,
        contentValidationErrors: validations.content?.errors,
        configurationsValidationErrors: validations.configurations?.errors,
      }

  return <RendererComponent {...rendererProps} {...additionalProps} />
}
